/**
 * Password validation utility for frontend
 * Mirrors the backend validation logic
 */

export const validatePasswordStrength = (password) => {
  const errors = [];
  
  // Check minimum length
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  
  // Check for uppercase letters
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  
  // Check for lowercase letters
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  
  // Check for numbers
  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  
  // Check for special characters
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)");
  }
  
  // Check for common weak patterns
  if (/(.)\1{2,}/.test(password)) {
    errors.push("Password cannot contain 3 or more consecutive identical characters");
  }
  
  // Check for common sequences
  const commonSequences = ['123', 'abc', 'qwe', 'asd', 'zxc'];
  const passwordLower = password.toLowerCase();
  for (const seq of commonSequences) {
    if (passwordLower.includes(seq)) {
      errors.push("Password cannot contain common sequences like '123', 'abc', etc.");
      break;
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const getPasswordStrengthScore = (password) => {
  let score = 0;
  const feedback = [];
  
  // Length scoring
  if (password.length >= 8) {
    score += 1;
    if (password.length >= 12) {
      score += 1;
    }
    if (password.length >= 16) {
      score += 1;
    }
  } else {
    feedback.push("Add more characters to reach minimum length");
  }
  
  // Character variety scoring
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add at least one uppercase letter (A-Z)");
  }
  
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add at least one lowercase letter (a-z)");
  }
  
  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add at least one number (0-9)");
  }
  
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    score += 1;
  } else {
    feedback.push("Add at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)");
  }
  
  // Bonus for mixed case and numbers
  if (/[A-Z].*[a-z]|[a-z].*[A-Z]/.test(password)) {
    score += 1;
  }
  
  if (/[A-Za-z].*\d|\d.*[A-Za-z]/.test(password)) {
    score += 1;
  }
  
  // Check for weak patterns
  if (/(.)\1{2,}/.test(password)) {
    feedback.push("Remove 3 or more consecutive identical characters");
  }
  
  const commonSequences = ['123', 'abc', 'qwe', 'asd', 'zxc'];
  const passwordLower = password.toLowerCase();
  for (const seq of commonSequences) {
    if (passwordLower.includes(seq)) {
      feedback.push("Avoid common sequences like '123', 'abc', etc.");
      break;
    }
  }
  
  // Determine strength level
  let level;
  if (score <= 2) {
    level = "weak";
  } else if (score <= 4) {
    level = "fair";
  } else if (score <= 6) {
    level = "good";
  } else if (score <= 8) {
    level = "strong";
  } else {
    level = "very_strong";
  }
  
  return {
    score,
    level,
    feedback,
    // The achievable max is 9: 3 for length (>=8, >=12, >=16) + 1 each for
    // upper/lower/digit/special + 1 for the mixed-case bonus + 1 for the
    // mixed-alnum bonus. This was hardcoded to 8, so a password meeting
    // every criterion (score 9) produced progress = (9 / 8) * 100 = 112.5%,
    // an out-of-range value handed to PasswordStrengthIndicator's
    // MUI LinearProgress (which expects 0-100).
    maxScore: 9
  };
};

export const getPasswordStrengthColor = (level) => {
  switch (level) {
    case 'weak':
      return '#f44336'; // Red
    case 'fair':
      return '#ff9800'; // Orange
    case 'good':
      return '#ffc107'; // Yellow
    case 'strong':
      return '#4caf50'; // Green
    case 'very_strong':
      return '#2e7d32'; // Dark Green
    default:
      return '#9e9e9e'; // Grey
  }
};

export const getPasswordStrengthText = (level) => {
  switch (level) {
    case 'weak':
      return 'Weak';
    case 'fair':
      return 'Fair';
    case 'good':
      return 'Good';
    case 'strong':
      return 'Strong';
    case 'very_strong':
      return 'Very Strong';
    default:
      return 'Very Weak';
  }
}; 